import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';
import { useToast } from '../components/layout/ToastProvider';
import FormModal from '../components/common/FormModal';
import ConfirmModal from '../components/common/ConfirmModal';

const { 
  FiBell, FiMail, FiMessageSquare, FiZap, FiPlus, FiSettings, 
  FiTrash2, FiClock, FiCheckCircle, FiAlertCircle, FiTrendingUp,
  FiFilter, FiSearch, FiMoreVertical, FiEye, FiMousePointer,
  FiCalendar, FiUser, FiActivity, FiGlobe, FiSmartphone
} = FiIcons;

const CHANNELS = [
  { id: 'email', label: 'Email', icon: FiMail, color: 'text-blue-400', bg: 'bg-blue-400/10' },
  { id: 'in_app', label: 'In-App', icon: FiBell, color: 'text-purple-400', bg: 'bg-purple-400/10' },
  { id: 'sms', label: 'SMS', icon: FiSmartphone, color: 'text-emerald-400', bg: 'bg-emerald-400/10' }
];

const TRIGGERS = [
  { id: 'enrollment', label: 'New Enrollment', icon: FiUser },
  { id: 'completion', label: 'Course Completion', icon: FiCheckCircle },
  { id: 'inactivity', label: 'Inactivity (7 Days)', icon: FiClock },
  { id: 'milestone', label: 'Milestone Achievement', icon: FiTrendingUp },
  { id: 'session_booked', label: 'Session Booked', icon: FiCalendar }
];

export default function Notifications() {
  const { addToast } = useToast();
  const [configs, setConfigs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('workflows'); // 'workflows' or 'delivery'
  
  // Modals
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configsRes, logsRes, templatesRes] = await Promise.all([
        supabase.from('notification_configs_2024').select('*').order('created_at', { ascending: false }),
        supabase.from('notification_logs_2024').select('*, client:clients_2024(name)').order('sent_at', { ascending: false }).limit(20),
        supabase.from('message_templates_2024').select('*')
      ]);

      if (configsRes.error) throw configsRes.error;
      setConfigs(configsRes.data || []);
      setLogs(logsRes.data || []);
      setTemplates(templatesRes.data || []);
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    const formData = new FormData(e.target);
    
    const payload = {
      title: formData.get('title'),
      trigger_type: formData.get('trigger_type'),
      channel: formData.get('channel'),
      template_id: formData.get('template_id') || null,
      delay_hours: parseInt(formData.get('delay_hours') || '0'),
      is_active: true,
      settings: {
        priority: formData.get('priority') || 'normal'
      }
    };

    try {
      if (selectedConfig) {
        const { error } = await supabase.from('notification_configs_2024').update(payload).eq('id', selectedConfig.id);
        if (error) throw error;
        addToast("Notification workflow updated");
      } else {
        const { error } = await supabase.from('notification_configs_2024').insert([payload]);
        if (error) throw error;
        addToast("New automated notification active");
      }
      setIsFormOpen(false);
      fetchData();
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const toggleConfigStatus = async (config) => {
    try {
      const { error } = await supabase
        .from('notification_configs_2024')
        .update({ is_active: !config.is_active })
        .eq('id', config.id);
      
      if (error) throw error;
      setConfigs(configs.map(c => c.id === config.id ? { ...c, is_active: !c.is_active } : c));
      addToast(`Workflow ${!config.is_active ? 'Activated' : 'Suspended'}`);
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleDeleteConfig = async () => {
    try {
      const { error } = await supabase.from('notification_configs_2024').delete().eq('id', selectedConfig.id);
      if (error) throw error;
      addToast("Workflow deleted");
      setIsDeleteOpen(false);
      fetchData();
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  if (loading) return (
    <div className="h-[60vh] flex flex-col items-center justify-center space-y-6">
      <div className="w-16 h-16 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
      <p className="text-slate-500 font-black text-[10px] uppercase tracking-[0.3em]">Calibrating Delivery Systems...</p>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-12 pb-32">
      {/* Premium Header */}
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 bg-slate-900/40 backdrop-blur-3xl p-10 rounded-[3rem] border border-white/5 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/5 blur-[120px] rounded-full -mr-48 -mt-48" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-400 border border-indigo-500/20">
              <SafeIcon icon={FiBell} />
            </div>
            <span className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.4em]">Automation Intelligence</span>
          </div>
          <h1 className="text-5xl font-black text-white tracking-tighter uppercase leading-none">Notifications</h1>
          <p className="text-slate-400 mt-4 text-lg font-medium max-w-xl leading-relaxed">Systematic client engagement through automated multi-channel communication.</p>
        </div>
        
        <button 
          onClick={() => { setSelectedConfig(null); setIsFormOpen(true); }}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-10 py-5 rounded-3xl font-black text-[11px] uppercase tracking-widest shadow-2xl shadow-indigo-600/30 transition-all flex items-center gap-3 transform hover:scale-105 active:scale-95 relative z-10"
        >
          <SafeIcon icon={FiPlus} className="text-lg" /> New Workflow
        </button>
      </header>

      {/* Analytics Insight */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          { label: 'Total Sent', val: '1,284', icon: FiZap, color: 'text-indigo-400', trend: '+12%' },
          { label: 'Open Rate', val: '64.2%', icon: FiEye, color: 'text-emerald-400', trend: '+5.4%' },
          { label: 'Click Rate', val: '18.9%', icon: FiMousePointer, color: 'text-amber-400', trend: '-2.1%' },
          { label: 'Active Flows', val: configs.filter(c => c.is_active).length, icon: FiActivity, color: 'text-purple-400', trend: 'STABLE' }
        ].map((stat, i) => (
          <div key={i} className="bg-white/[0.02] border border-white/10 p-8 rounded-[2.5rem] flex items-center justify-between backdrop-blur-xl group hover:border-white/20 transition-all">
             <div>
               <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">{stat.label}</p>
               <h3 className="text-3xl font-black text-white tracking-tight">{stat.val}</h3>
               <p className={`text-[8px] font-bold mt-1 ${stat.trend.startsWith('+') ? 'text-emerald-400' : stat.trend === 'STABLE' ? 'text-slate-500' : 'text-rose-400'}`}>
                 {stat.trend} <span className="text-slate-600 ml-1">vs last month</span>
               </p>
             </div>
             <div className={`w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center ${stat.color} group-hover:scale-110 transition-transform border border-white/5`}>
               <SafeIcon icon={stat.icon} className="text-xl" />
             </div>
          </div>
        ))}
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-3 p-2 bg-white/[0.02] border border-white/10 rounded-[2rem] w-fit">
        {[
          { id: 'workflows', label: 'Automation Workflows', icon: FiZap },
          { id: 'delivery', label: 'Delivery Center', icon: FiActivity }
        ].map(tab => (
          <button 
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-8 py-4 rounded-[1.5rem] text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-3 ${activeTab === tab.id ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-600/20' : 'text-slate-500 hover:text-slate-300'}`}
          >
            <SafeIcon icon={tab.icon} /> {tab.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'workflows' ? (
          <motion.div 
            key="workflows"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {configs.map((config, idx) => (
              <motion.div 
                key={config.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className={`bg-white/[0.02] border border-white/10 p-10 rounded-[3.5rem] relative overflow-hidden group backdrop-blur-3xl transition-all ${!config.is_active ? 'opacity-60 grayscale' : 'hover:bg-white/[0.05] hover:border-white/20'}`}
              >
                <div className="flex justify-between items-start mb-10">
                   <div className={`w-16 h-16 rounded-[2rem] flex items-center justify-center border border-white/5 shadow-2xl ${CHANNELS.find(c => c.id === config.channel)?.bg} ${CHANNELS.find(c => c.id === config.channel)?.color}`}>
                      <SafeIcon icon={CHANNELS.find(c => c.id === config.channel)?.icon || FiBell} className="text-2xl" />
                   </div>
                   <div className="flex gap-2">
                     <button onClick={() => { setSelectedConfig(config); setIsFormOpen(true); }} className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center text-slate-500 hover:text-white transition-all">
                       <SafeIcon icon={FiSettings} />
                     </button>
                     <button onClick={() => { setSelectedConfig(config); setIsDeleteOpen(true); }} className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center text-slate-500 hover:text-rose-500 transition-all">
                       <SafeIcon icon={FiTrash2} />
                     </button>
                   </div>
                </div>

                <div className="space-y-4 mb-10">
                  <h3 className="text-2xl font-black text-white tracking-tighter uppercase leading-tight">{config.title}</h3>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-4 py-1.5 bg-white/5 rounded-full text-[8px] font-black text-slate-400 uppercase tracking-widest border border-white/5 flex items-center gap-2">
                      <SafeIcon icon={TRIGGERS.find(t => t.id === config.trigger_type)?.icon || FiActivity} />
                      {TRIGGERS.find(t => t.id === config.trigger_type)?.label || config.trigger_type}
                    </span>
                    {config.delay_hours > 0 && (
                      <span className="px-4 py-1.5 bg-amber-500/10 rounded-full text-[8px] font-black text-amber-400 uppercase tracking-widest border border-amber-500/20 flex items-center gap-2">
                        <SafeIcon icon={FiClock} /> {config.delay_hours}h Delay
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-8 border-t border-white/5">
                   <button 
                    onClick={() => toggleConfigStatus(config)}
                    className={`text-[9px] font-black uppercase tracking-[0.2em] flex items-center gap-2 transition-all ${config.is_active ? 'text-emerald-400' : 'text-slate-500'}`}
                   >
                     <div className={`w-3 h-3 rounded-full ${config.is_active ? 'bg-emerald-500 animate-pulse' : 'bg-slate-700'}`} />
                     {config.is_active ? 'System Active' : 'Workflow Paused'}
                   </button>
                   <div className="flex items-center gap-1.5">
                     <span className="text-[9px] font-black text-slate-600 uppercase tracking-widest">Total: 428</span>
                   </div>
                </div>
              </motion.div>
            ))}
            
            {configs.length === 0 && (
              <div className="col-span-full py-32 bg-white/[0.01] border border-dashed border-white/10 rounded-[4rem] text-center">
                 <div className="w-20 h-20 bg-white/5 rounded-[2.5rem] flex items-center justify-center text-slate-600 mx-auto mb-6 border border-white/5">
                   <SafeIcon icon={FiZap} className="text-3xl" />
                 </div>
                 <h4 className="text-xl font-black text-white uppercase tracking-tight mb-2">No Active Workflows</h4>
                 <p className="text-slate-500 max-w-sm mx-auto text-sm font-medium">Capture engagement opportunities by creating your first automated notification workflow.</p>
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div 
            key="delivery"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="bg-white/[0.02] border border-white/10 rounded-[3rem] overflow-hidden backdrop-blur-3xl"
          >
            <div className="p-8 border-b border-white/5 flex justify-between items-center bg-white/[0.01]">
               <h3 className="text-lg font-black text-white uppercase tracking-tight flex items-center gap-3">
                 <SafeIcon icon={FiActivity} className="text-indigo-400" /> Real-time Delivery Stream
               </h3>
               <div className="flex gap-4">
                 <div className="relative">
                   <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 text-xs" />
                   <input placeholder="Filter by client..." className="bg-black/40 border border-white/5 rounded-xl pl-10 pr-4 py-2 text-[10px] text-white outline-none focus:ring-1 focus:ring-indigo-500/50 w-64" />
                 </div>
               </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest">Client / Delivery Node</th>
                    <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest">Channel</th>
                    <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest">Subject</th>
                    <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest">Status</th>
                    <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, idx) => (
                    <tr key={log.id} className="hover:bg-white/[0.02] transition-colors group">
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-4">
                           <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 font-black text-xs">
                             {log.client?.name?.[0] || 'U'}
                           </div>
                           <div>
                             <p className="text-sm text-white font-bold">{log.client?.name || 'Unknown Client'}</p>
                             <p className="text-[10px] text-slate-500 font-mono italic">ID: {log.client_id.slice(0, 8)}</p>
                           </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${CHANNELS.find(c => c.id === log.channel)?.bg} ${CHANNELS.find(c => c.id === log.channel)?.color}`}>
                          <SafeIcon icon={CHANNELS.find(c => c.id === log.channel)?.icon || FiBell} />
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <p className="text-sm text-slate-300 font-medium max-w-xs truncate">{log.subject}</p>
                      </td>
                      <td className="px-8 py-6">
                        <div className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest border border-white/5 inline-flex items-center gap-2 ${
                          log.status === 'opened' || log.status === 'clicked' ? 'bg-emerald-500/10 text-emerald-400' :
                          log.status === 'failed' ? 'bg-rose-500/10 text-rose-400' : 'bg-blue-500/10 text-blue-400'
                        }`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${
                             log.status === 'opened' || log.status === 'clicked' ? 'bg-emerald-500' :
                             log.status === 'failed' ? 'bg-rose-500' : 'bg-blue-500'
                          }`} />
                          {log.status}
                        </div>
                      </td>
                      <td className="px-8 py-6 text-right">
                        <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{new Date(log.sent_at).toLocaleTimeString()}</p>
                        <p className="text-[8px] text-slate-700 font-bold">{new Date(log.sent_at).toLocaleDateString()}</p>
                      </td>
                    </tr>
                  ))}
                  {logs.length === 0 && (
                    <tr>
                      <td colSpan="5" className="py-20 text-center">
                        <p className="text-[10px] font-black text-slate-600 uppercase tracking-widest">No delivery metrics recorded in the current cycle.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Workflow Builder Modal */}
      <FormModal 
        isOpen={isFormOpen} 
        onClose={() => setIsFormOpen(false)} 
        title={selectedConfig ? "Edit Notification Strategy" : "Design Engagement Workflow"} 
        onSubmit={handleSaveConfig}
        loading={modalLoading}
        width="max-w-4xl"
      >
        <div className="space-y-12 max-h-[75vh] overflow-y-auto pr-6 custom-scrollbar pb-10">
          {/* Identity Section */}
          <section className="space-y-8">
            <div className="flex items-center gap-4 border-b border-white/5 pb-4">
              <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-400">
                <SafeIcon icon={FiSettings} />
              </div>
              <h3 className="text-lg font-black text-white uppercase tracking-tight">Workflow Parameters</h3>
            </div>
            
            <div className="space-y-3">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Workflow Title</label>
              <input name="title" defaultValue={selectedConfig?.title} required className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-bold outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all" placeholder="e.g. 7-Day Inactivity Re-engagement" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Engagement Trigger</label>
                <select name="trigger_type" defaultValue={selectedConfig?.trigger_type} className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-bold outline-none focus:ring-2 focus:ring-indigo-500/50 appearance-none">
                  {TRIGGERS.map(t => <option key={t.id} value={t.id}>{t.label.toUpperCase()}</option>)}
                </select>
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Delivery Channel</label>
                <select name="channel" defaultValue={selectedConfig?.channel} className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-bold outline-none focus:ring-2 focus:ring-indigo-500/50 appearance-none">
                  {CHANNELS.map(c => <option key={c.id} value={c.id}>{c.label.toUpperCase()}</option>)}
                </select>
              </div>
            </div>
          </section>

          {/* Logistics Section */}
          <section className="space-y-8">
            <div className="flex items-center gap-4 border-b border-white/5 pb-4">
              <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-400">
                <SafeIcon icon={FiClock} />
              </div>
              <h3 className="text-lg font-black text-white uppercase tracking-tight">Delivery Logistics</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Delay (Hours)</label>
                <input name="delay_hours" type="number" defaultValue={selectedConfig?.delay_hours || 0} className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-bold outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all" placeholder="0 = Immediate" />
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Priority Protocol</label>
                <select name="priority" defaultValue={selectedConfig?.settings?.priority || 'normal'} className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-bold outline-none focus:ring-2 focus:ring-indigo-500/50 appearance-none">
                  <option value="high">HIGH PRIORITY (CRITICAL)</option>
                  <option value="normal">STANDARD DELIVERY</option>
                  <option value="low">LOW PRIORITY (BACKGROUND)</option>
                </select>
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Content Intelligence (Template)</label>
              <select name="template_id" defaultValue={selectedConfig?.template_id} className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-bold outline-none focus:ring-2 focus:ring-indigo-500/50 appearance-none">
                <option value="">MANUAL CONTENT PROTOCOL</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.title.toUpperCase()} ({t.category})</option>)}
              </select>
            </div>
          </section>
        </div>
      </FormModal>

      <ConfirmModal 
        isOpen={isDeleteOpen} 
        onClose={() => setIsDeleteOpen(false)} 
        onConfirm={handleDeleteConfig}
        title="Protocol: Decommission Workflow" 
        message="Permanently terminate this automated notification workflow? All historical delivery data for this config will be detached."
      />

      {/* Floating System Status */}
      <motion.div 
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        className="fixed bottom-10 left-1/2 -translate-x-1/2 z-50 px-10 py-5 bg-slate-900/80 backdrop-blur-3xl border border-white/10 rounded-full shadow-2xl flex items-center gap-8"
      >
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
          <span className="text-[10px] font-black text-white uppercase tracking-widest">Delivery Nodes Online</span>
        </div>
        <div className="h-5 w-px bg-white/10" />
        <div className="flex items-center gap-3">
          <SafeIcon icon={FiGlobe} className="text-indigo-400" />
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Global CDN Active</span>
        </div>
      </motion.div>
    </div>
  );
}